import { useTheme } from '@/src/context/ThemeContext';
import { UseCaseLandingConfig, UseCaseLandingScreen } from '@/src/presentation/screens/UseCaseLandingScreen';
import Head from 'expo-router/head';
import { useRouter } from 'expo-router';

const PAGE_URL = 'https://tournaly.app/swiss-system-tournament';
const OG_IMAGE = 'https://tournaly.app/og-image.png';

const CONFIG: UseCaseLandingConfig = {
  badge: 'Swiss System · Pro',
  badgeEs: 'Sistema Suizo · Pro',
  badgeIcon: 'shuffle-outline',
  title: 'Swiss System',
  titleEs: 'Torneos con',
  titleAccent: 'Tournament Maker',
  titleAccentEs: 'Sistema Suizo',
  subtitle:
    'Score-based pairing with no eliminations. Players face opponents of similar strength each round — the fairest format for large groups where everyone deserves to keep playing.',
  subtitleEs:
    'Emparejamiento por puntuación sin eliminaciones. Los jugadores se enfrentan a rivales de nivel similar en cada ronda — el formato más justo para grupos grandes donde todos merecen seguir jugando.',
  isPro: true,
  steps: [
    {
      title: 'Set the number of rounds',
      titleEs: 'Define el número de rondas',
      desc: 'Choose how many rounds to play. Swiss works best with log₂(players) + 1 rounds.',
      descEs: 'Elige cuántas rondas jugar. El sistema suizo funciona mejor con log₂(jugadores) + 1 rondas.',
    },
    {
      title: 'Each round auto-pairs by score',
      titleEs: 'Cada ronda empareja por puntuación',
      desc: 'After round 1, players with similar scores are matched — equal competition every round.',
      descEs: 'Tras la primera ronda, los jugadores con puntuaciones similares se emparejan — competición equilibrada cada ronda.',
    },
    {
      title: 'Final standings rank all players',
      titleEs: 'La clasificación final ordena a todos',
      desc: 'No eliminations — everyone plays all rounds. The final standings reflect true performance.',
      descEs: 'Sin eliminaciones — todos juegan todas las rondas. La clasificación final refleja el rendimiento real.',
    },
  ],
  features: [
    {
      icon: 'shuffle-outline',
      color: '#FF6B35',
      title: 'Score-Based Pairing',
      titleEs: 'Emparejamiento por Puntuación',
      desc: 'Each round, players with the same score are matched — no mismatches, maximum fairness.',
      descEs: 'Cada ronda, los jugadores con la misma puntuación se emparejan — sin desequilibrios, máxima justicia.',
    },
    {
      icon: 'people-outline',
      color: '#4ECDC4',
      title: 'No Eliminations',
      titleEs: 'Sin Eliminaciones',
      desc: 'Everyone plays every round. Swiss is ideal for large groups and long-term skill measurement.',
      descEs: 'Todos juegan cada ronda. El sistema suizo es ideal para grupos grandes y medir habilidades a largo plazo.',
    },
    {
      icon: 'options-outline',
      color: '#45B7D1',
      title: 'Flexible Rounds',
      titleEs: 'Rondas Flexibles',
      desc: 'Set any number of rounds — play a quick 3-round event or a deep 9-round championship.',
      descEs: 'Define cualquier número de rondas — organiza un evento rápido de 3 rondas o un campeonato de 9.',
    },
    {
      icon: 'bar-chart-outline',
      color: '#10B981',
      title: 'Live Standings',
      titleEs: 'Clasificación en Vivo',
      desc: 'Full standings with points, wins and tiebreakers updated in real time.',
      descEs: 'Clasificación completa con puntos, victorias y desempates actualizada en tiempo real.',
    },
    {
      icon: 'share-social-outline',
      color: '#06B6D4',
      title: 'Export Results',
      titleEs: 'Exportar Resultados',
      desc: 'Share the standings table as PNG or export to PDF for official records.',
      descEs: 'Comparte la tabla de clasificación como PNG o expórtala a PDF para registros oficiales.',
    },
    {
      icon: 'phone-portrait-outline',
      color: '#A78BFA',
      title: 'Mobile Apps',
      titleEs: 'Apps Móviles',
      desc: 'Run your Swiss tournament from the iOS or Android app — anywhere, anytime.',
      descEs: 'Organiza tu torneo suizo desde la app de iOS o Android — en cualquier lugar y momento.',
    },
  ],
  relatedBlogSlug: '2026-03-06-swiss-system-tournament-guide',
  relatedBlogTitle: 'Swiss System Tournament Guide — How It Works & When to Use It',
  relatedBlogTitleEs: 'Guía del Sistema Suizo — Cómo Funciona y Cuándo Usarlo',
  relatedBlogSummary: 'A complete guide to the Swiss system: pairing rules, tiebreakers, number of rounds and ideal use cases.',
  relatedBlogSummaryEs: 'Una guía completa del sistema suizo: reglas de emparejamiento, desempates, número de rondas y casos de uso ideales.',
};

const title = 'Swiss System Tournament Maker — Free Online Tool | Tournaly';
const description =
  'Run Swiss system tournaments online. Score-based pairing, no eliminations, flexible rounds and live standings. Included in Tournaly Pro.';

export default function SwissSystemTournamentPage() {
  const { colors, isDark, toggleTheme } = useTheme();
  const router = useRouter();

  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Tournaly — Swiss System Tournament Maker',
    applicationCategory: 'SportsApplication',
    operatingSystem: 'Web, iOS, Android',
    url: PAGE_URL,
    description,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@type': 'Organization', name: 'Tournaly', url: 'https://tournaly.app' },
  });

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Tournaly" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="keywords" content="swiss system tournament, swiss tournament bracket, swiss pairing, swiss chess format, tournament maker" />
        <script type="application/ld+json">{jsonLd}</script>
      </Head>
      <UseCaseLandingScreen
        config={CONFIG}
        isDark={isDark}
        onToggleTheme={toggleTheme}
        onGetStarted={() => router.push('/auth/register' as any)}
        onSignIn={() => router.push('/auth/login' as any)}
        onBlogPostPress={(slug) => {
          if (slug === 'blog') router.push('/blog' as any);
          else if (slug === 'privacy') router.push('/privacy' as any);
          else if (slug === 'terms') router.push('/terms' as any);
          else router.push(`/blog/${slug}` as any);
        }}
        colors={colors}
      />
    </>
  );
}
